import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { plainToInstance } from 'class-transformer';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { finalize, map, shareReplay } from 'rxjs/operators';

import { Account } from '../auth/models/account.model';
import { SlotsRequest } from '../company/models/slots-request.model';
import { Negotiation } from '../company/modules/commercial/models/negotiation.model';
import { Contract } from '../company/modules/contracts/models/contract.model';
import { JSend } from '../models/jsend.model';
import { STOPslot, Slot, SlotDetails, SlotStats, StopStatus, StopSynch } from '../models/slot.model';
import { CPEActors, SlotHistory, SlotsBatch } from '../models/slots-batch.model';
import { endOfDay, startOfDay } from '../utilities/date';
import { buildFilters } from '../utilities/filters';
import { simpleHash } from '../utilities/json-tools';
import { CacheService } from './cache.service';
import { PusherService } from './pusher.service';

@Injectable({
  providedIn: 'root'
})
export class SlotService {

  private baseUrl = '/:apiBase/companies/:companyId';
  private batchesUrl = '/:apiBatches/companies/:companyId';
  private slotsPath = this.baseUrl + '/slots';
  private slotsExportPath = this.batchesUrl + '/slots/export';
  private slotByIdPath = this.slotsPath + '/:slotId';
  private batchesPath = this.baseUrl + '/batches';
  private batchByIdPath = this.batchesPath + '/:batchId';
  private batchNegotiationPath = this.batchByIdPath + '/negotiation/:negotiationId';
  private batchContractPath = this.batchByIdPath + '/contract/:contractId';
  private batchTakePath = this.batchByIdPath + '/take';
  private CPEActorsPath = this.batchByIdPath + '/cpe-actors';
  private misTurnosActivosSimple = this.baseUrl + '/stop/misturnosactivossimple/:from/:to';
  private refreshPath = this.baseUrl + '/stop/sync'
  // private syncSinglePath = this.baseUrl + '/stop/sync-single'
  private syncMultiplePath = this.baseUrl + '/stop/sync-multiple'
  private syncAllPath = this.baseUrl + '/stop/sync-all'
  private slotsStats = this.baseUrl + '/slot-stats';
  private verifyBatchesExistencePath = this.batchesPath + '/exists';
  private stopStatusPath = '/:apiBase/v1/companies/:companyId/stop/status';

  private _collectionSubjects: { [eventKey: string]: BehaviorSubject<{ body: SlotsBatch[]; headers: HttpHeaders }> } = {};
  private _itemSubjects: { [batchId: number]: BehaviorSubject<SlotsBatch> } = {};
  private _sCollectionSubjects: { [eventKey: string]: BehaviorSubject<{ body: SlotDetails[]; headers: HttpHeaders }> } = {};
  private _statsCollectionSubjects: { [eventKey: string]: BehaviorSubject<SlotStats[]> } = {};
  private _stopStatuses: StopStatus[];
  /**
   * Maps only those parameters that don't match in the API call.
   * Format - 'Webapp query': 'API query'
   */
  private readonly queryMap: Record<string, string> = {
    'product_id': 'filters[product][id]',
    'status_id': 'filters[status][id]',
    'validity': 'filters[validity]',
    // FAS-2757
    'allocator_name': 'filters[allocator_name]',
    'recipient_name': 'filters[recipient_name]',
    'assigned_to': 'filters[assigned_to]',
    'sustainable': 'filters[sustainable]',
    'date_issued': 'filters[date_issued]',
    'zone_id': 'filters[zone_id]',
    'destination_id': 'filters[destination_id]',
    'zone_name': 'filters[zone_name]',
  };

  constructor(
    private http: HttpClient,
    private cacheService: CacheService,
    private pusherService: PusherService
  ) { }

  public create(companyId: number, account: Account, slotsBatches: SlotsBatch[], slotRequest: SlotsRequest = null): Observable<any> {
    const url = this.batchesPath
      .replace(':companyId', companyId.toString());

    const data = {
      batches: slotsBatches,
      account: account,
      /**
       * is_front => This hotfix prevents the creation of batches from other 
       * sides than the agreed ones, e.g. integrations or external API calls.
       */
      is_front: true
    };

    if (slotRequest) {
      data["slot_request"] = slotRequest;
    }
    return this.http.post(url, data);
  }

  public getHistory(companyId: number, slotId: number): Observable<SlotHistory[]> {
    const url = this.slotByIdPath
      .replace(':companyId', companyId.toString())
      .replace(':slotId', slotId.toString());

    return this.http.get<SlotHistory[]>(url).pipe(map(response => {
      return plainToInstance(SlotHistory, response);
    }));
  }

  private getBatches(companyId: number, filters?: any, paginated?: boolean): Observable<{ body: SlotsBatch[], headers: HttpHeaders }> {
    if (paginated && !filters?.page) filters = { ...filters, page: 1 };

    let url = this.batchesPath
      .replace(':companyId', companyId.toString());

    url = buildFilters(url, filters, this.queryMap, ['validity']);

    return this.http.get<SlotsBatch[]>(url, { observe: 'response' }).pipe(map(response => {
      return { body: plainToInstance(SlotsBatch, response.body), headers: response.headers };
    }));
  }

  public get(companyId: number, batchId: number): Observable<SlotsBatch> {
    const url = this.batchByIdPath
      .replace(':companyId', companyId.toString())
      .replace(':batchId', batchId.toString());

    return this.http.get<SlotsBatch>(url).pipe(map(response => {
      return plainToInstance(SlotsBatch, response);
    }));
  }

  private getSlots(companyId: number, filters?: any, paginated?: boolean): Observable<{ body: SlotDetails[], headers: HttpHeaders }> {
    if (paginated && !filters?.page) filters = { ...filters, page: 1 };

    // https://agreemarket.atlassian.net/browse/FAS-2798
    let url = this.slotsPath
      .replace(':companyId', companyId.toString());

    url = buildFilters(url, filters, this.queryMap, ['validity']);

    return this.http.get<SlotDetails[]>(url, { observe: 'response' }).pipe(map(response => {
      return { body: plainToInstance(SlotDetails, response.body), headers: response.headers };
    }));
  }

  public update(companyId: number, account: Account, batchId: number, status: any, slotsBatch?: SlotsBatch): Observable<any> {
    const url = this.batchByIdPath
      .replace(':companyId', companyId.toString())
      .replace(':batchId', batchId.toString());

    const data = {
      'status': status,
      'account': account
    };

    switch (status.id) {
      case 5:
        data['batch_returned'] = slotsBatch;
        break;
      default:
        data['batch_rejected'] = slotsBatch;
        break;
    }

    return this.http.post(url, data);
  }

  public deleteSlots(companyId: number, slotIds: number[]): Observable<any> {
    const url = this.slotsPath
      .replace(':companyId', companyId.toString());

    // Params Object
    const params = { ids: JSON.stringify(slotIds) };

    return this.http.delete<any>(url, { params: params });
  }

  /**
   * Accepts and assigns the entire {@link SlotsBatch} to the specified
   * {@link Company}.
   */
  public take(companyId: number, account: Account, batchId: number): Observable<SlotsBatch> {
    const url = this.batchTakePath
      .replace(':companyId', companyId.toString())
      .replace(':batchId', batchId.toString());

    const data = {
      'account': account
    };

    return this.http.post<SlotsBatch>(url, data).pipe(map(response => {
      return plainToInstance(SlotsBatch, response);
    }));
  }

  public watch(companyId: number, batchId: number): Observable<SlotsBatch> {
    return this.pusherService.subjectManager(
      {
        collection: this._itemSubjects,
        key: batchId,
        getData: () => this.get(companyId, batchId)
      },
      {
        channel: `company_${companyId}`,
        event: 'batch',
        dueTime: 2000,
        condition: (event: any) => event?.id === batchId
      });
  }

  public watchBatches(companyId: number, filters?: any, paginated: boolean = true): Observable<{ body: SlotsBatch[], headers: HttpHeaders }> {
    const eventKey = simpleHash([companyId, filters, paginated]);

    return this.pusherService.subjectManager(
      {
        collection: this._collectionSubjects,
        key: eventKey,
        getData: () => this.getBatches(companyId, filters, paginated)
      },
      {
        channel: `company_${companyId}`,
        event: 'batch',
        dueTime: 2000
      }
    );
  }

  public watchSlots(companyId: number, filters?: any, paginated: boolean = true): Observable<{ body: SlotDetails[], headers: HttpHeaders }> {
    const eventKey = simpleHash([companyId, filters, paginated]);

    return this.pusherService.subjectManager(
      {
        collection: this._sCollectionSubjects,
        key: eventKey,
        getData: () => this.getSlots(companyId, filters, paginated)
      },
      {
        channel: `company_${companyId}`,
        event: 'batch',
        dueTime: 2000
      }
    );
  }

  public updateNegotiation(companyId: number, slotsBatch: SlotsBatch, negotiation: Negotiation): Observable<SlotsBatch> {
    const url = this.batchNegotiationPath
      .replace(':companyId', companyId.toString())
      .replace(':batchId', slotsBatch.id.toString())
      .replace(':negotiationId', negotiation.id.toString());

    return this.http.post<SlotsBatch>(url, {}).pipe(map(response => {
      return plainToInstance(SlotsBatch, response);
    }));
  }

  public updateContract(companyId: number, slotsBatch: SlotsBatch, contract: Contract): Observable<SlotsBatch> {
    const url = this.batchContractPath
      .replace(':companyId', companyId.toString())
      .replace(':batchId', slotsBatch.id.toString())
      .replace(':contractId', contract.id.toString());

    return this.http.post<SlotsBatch>(url, {}).pipe(map(response => {
      return plainToInstance(SlotsBatch, response);
    }));
  }

  public deleteNegotiation(companyId: number, slotsBatch: SlotsBatch): Observable<SlotsBatch> {
    const url = this.batchNegotiationPath
      .replace(':companyId', companyId.toString())
      .replace(':batchId', slotsBatch.id.toString())
      .replace(':negotiationId', slotsBatch.negotiation.id.toString());

    return this.http.delete<SlotsBatch>(url).pipe(map(response => {
      return plainToInstance(SlotsBatch, response);
    }));
  }

  public deleteContract(companyId: number, slotsBatch: SlotsBatch): Observable<SlotsBatch> {
    const url = this.batchContractPath
      .replace(':companyId', companyId.toString())
      .replace(':batchId', slotsBatch.id.toString())
      .replace(':contractId', slotsBatch.contract.id.toString());

    return this.http.delete<SlotsBatch>(url).pipe(map(response => {
      return plainToInstance(SlotsBatch, response);
    }));
  }

  public export(companyId: number, range: Date[], filters: any = {}): Observable<any> {
    if (range && range.length === 2) {
      let url = this.slotsExportPath
        .replace(':companyId', companyId.toString());

      if ('validity' in filters) {
        delete filters['validity'];
      }

      const from = startOfDay(new Date(range[0])); // start of the day
      const to = endOfDay(new Date(range[1]));

      filters['filters[date]'] = 'between:' + from.toISOString() + ',' + to.toISOString();

      url = buildFilters(url, filters, this.queryMap);

      return this.http.get(url);
    }
  }

  public getActors(companyId: number, batchId: number): Observable<CPEActors> {
    const url = this.CPEActorsPath
      .replace(':companyId', companyId.toString())
      .replace(':batchId', batchId.toString());

    const cached = this.cacheService.get(url);
    // Return cached value if exists
    if (cached) return of(plainToInstance(CPEActors, cached));

    // Check if there's already a request in flight for this URL
    const inFlight = this.cacheService.getInFlight(url);
    if (inFlight) return inFlight;

    // Create and store the in-flight request
    const request$ = this.http.get<CPEActors>(url).pipe(
      map(response => {
        this.cacheService.set(url, response, 60 * 8);
        return plainToInstance(CPEActors, response);
      }),
      shareReplay(1), // Share the result with all subscribers
      finalize(() => this.cacheService.clearInFlight(url)) // Clean up when done
    );

    this.cacheService.setInFlight(url, request$);
    return request$;
  }

  public getSTOPSlots(companyId: number, from: Date, to: Date): Observable<STOPslot[]> {
    const url = this.misTurnosActivosSimple
      .replace(':companyId', companyId.toString())
      .replace(':from', from.toISOString().split('T')[0])
      .replace(':to', to.toISOString().split('T')[0]);

    const stream = this.http.get<{
      count: number;
      data: STOPslot[];
      isError: boolean;
      ticks: number;
    }>(url);

    return stream.pipe(map(response => {
      return plainToInstance(STOPslot, response.data);
    }));
  }

  private getSats(companyId: number, filters?: any): Observable<SlotStats[]> {
    let url = this.slotsStats
      .replace(':companyId', companyId.toString());

    url = buildFilters(url, filters, this.queryMap);

    return this.http.get<SlotStats[]>(url).pipe(map(response => {
      return response.map(item => plainToInstance(SlotStats, item));
    }));
  }

  public watchStats(companyId: number, filters?: any): Observable<SlotStats[]> {
    const eventKey = simpleHash([companyId, filters]);

    return this.pusherService.subjectManager(
      {
        collection: this._statsCollectionSubjects,
        key: eventKey,
        getData: () => this.getSats(companyId, filters)
      },
      {
        channel: `company_${companyId}`,
        event: 'batch',
        dueTime: 2000
      },
      null
    );
  }

  public refreshSTOPstatus(companyId: number): Observable<number> {
    const url = this.refreshPath.replace(':companyId', companyId.toString());

    return this.http.post<{ minutes_left: number }>(url, {}).pipe(map(response => {
      return response.minutes_left || 0.25;
    }));
  }

  /**
   * Verifica la existencia de {@link Slot|Slots} en el sistema y detecta duplicados.
   *
   * Este método realiza dos validaciones:
   * 1. Consulta al backend para obtener los slots que ya existen en la base de datos
   *    (basándose en la propiedad `reference` de cada slot).
   * 2. Detecta duplicados locales entre los batches proporcionados (slots con la misma
   *    `reference` que aparecen más de una vez en el conjunto de datos enviado).
   *
   * El resultado combina ambas validaciones, retornando un único array con:
   * - Los slots que ya existen en el sistema (respuesta del backend).
   * - Los slots con referencias duplicadas entre los batches proporcionados
   *   (que no estén ya incluidos en la respuesta del backend).
   *
   * @param {number} companyId - ID de la empresa.
   * @param {Account} account - Cuenta del usuario que realiza la verificación.
   * @param {SlotsBatch[]} slotsBatches - Array de batches con los slots a verificar
   *        (cada batch debe tener la propiedad `unassigned` con los slots).
   * @returns {Observable<Slot[]>} Observable con el array de slots existentes y/o duplicados.
   */
  public verifyBatchesExistence(companyId: number, account: Account, slotsBatches: SlotsBatch[]): Observable<Slot[]> {
    const url = this.verifyBatchesExistencePath
      .replace(':companyId', companyId.toString());

    const data = {
      batches: slotsBatches,
      account: account
    };

    // Extract all slots from batches
    const allSlots: Slot[] = slotsBatches.flatMap(batch => batch.unassigned || []);

    // Find duplicate references within the provided batches
    const referenceCounts = new Map<string, number>();
    allSlots.forEach(slot => {
      if (slot.reference) {
        referenceCounts.set(slot.reference, (referenceCounts.get(slot.reference) || 0) + 1);
      }
    });

    const duplicateReferences = new Set<string>();
    referenceCounts.forEach((count, reference) => {
      if (count > 1) {
        duplicateReferences.add(reference);
      }
    });

    // Get slots with duplicate references (one per reference)
    const localDuplicates: Slot[] = [];
    const addedReferences = new Set<string>();
    allSlots.forEach(slot => {
      if (slot.reference && duplicateReferences.has(slot.reference) && !addedReferences.has(slot.reference)) {
        localDuplicates.push(slot);
        addedReferences.add(slot.reference);
      }
    });

    return this.http.post<Slot[]>(url, data).pipe(map(response => {
      const existingSlots = plainToInstance(Slot, response);

      // Merge local duplicates that are not already in the backend response
      const existingReferences = new Set(existingSlots.map(s => s.reference));
      const newDuplicates = localDuplicates.filter(s => !existingReferences.has(s.reference));

      return [...existingSlots, ...newDuplicates];
    }));
  }

  /**
    * Retrieves and caches the STOP statuses for a given company ID.
    *
    * @param {number} companyId - The ID of the company.
    * @returns {Observable<StopStatus[]>} - An observable of the STOP statuses.
    */
  public getSTOPStatuses(companyId: number): Observable<StopStatus[]> {
    if (this._stopStatuses) {
      return of(this._stopStatuses);
    }

    // TODO: companyId is irrelevant, STOP statuses are cross-company.
    const url = this.stopStatusPath.replace(':companyId', companyId.toString());

    return this.http.get<StopStatus[]>(url).pipe(map(response => {
      // Cache response
      this._stopStatuses = plainToInstance(StopStatus, response);
      return this._stopStatuses;
    }));
  }

  /**
   * Sends a synchronization request for a single company reference and cuit.
   * 
   * @param {number} companyId - The ID of the company.
   * @param {string} reference - The reference code for synchronization.
   * @param {string} cuit - The CUIT identifier.
   * @returns {Observable<any>} Observable containing the response of the synchronization request.
   */
  // public STOPsyncSingle(companyId: number, reference: string, cuit: string): Observable<any> {
  //   return this.sendSyncRequest(this.syncSinglePath, companyId, { reference, cuit });
  // }

  /**
   * Sends a synchronization request for multiple company slots.
   * 
   * @param {number} companyId - The ID of the company.
   * @param {Array<{ reference: string, cuit: string }>} slots - Array of objects with reference and cuit for each slot.
   * @returns {Observable<any>} Observable containing the response of the synchronization request.
   */
  public STOPsyncMultiple(companyId: number, slots: { reference: string, cuit: string, date: Date }[]): Observable<StopSynch> {
    const url = this.syncMultiplePath
      .replace(':companyId', companyId.toString());

    return this.http.post<JSend<StopSynch>>(url, { slots })
      .pipe(map(response => plainToInstance(StopSynch, response.data)));
  }

  public STOPsyncAll(companyId: number, interval?: number): Observable<any> {
    const url = this.syncAllPath
      .replace(':companyId', companyId.toString());

    return this.http.post<JSend<StopSynch>>(url, { interval: interval });
  }
}

